import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import { yupResolver } from "@hookform/resolvers/yup"
import * as yup from "yup"
import { createEmpOption } from "../../../apis/options/EmpOptionApi";
import { useState } from "react";
import { getIssueGroup } from "../../../apis/options/IssueGroupApi";
import { useHistory, useParams } from "react-router-dom";
import moment from "moment";
import EmployeeSearchAdmin from "../../common/empsearch/EmployeeSearchAdmin";
import { Card, Container, Row, Col, Spinner } from "react-bootstrap";

const schema = yup
  .object({
    employeeId: yup.number()
      .integer().required(),
    issueGroupId: yup.number().required().positive().integer(),
    issueDate: yup.date().required(),
    totalShares: yup.number().required().integer().positive()
  })
  .required()


export default function AddEmpOptionForm() {

  let { planId, groupId } = useParams();
  let history = useHistory();

  const [group, setGroup] = useState(null);
  const [isDataLoading, setIsDataLoading] = useState(true);

  const [issueDate, setIssueDate] = useState(null);
  const [selectedEmp, setSelectedEmp] = useState(null);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  })

  useEffect(() => {
    async function getData(gid) {
      const result = await getIssueGroup(gid);
      setGroup(result);
      newIssueDate(result.defaultIssueDate);

      setIsDataLoading(false);
    }

    try {
      getData(groupId);
    }
    catch(error){
      console.log(error);
    }
  }, [groupId]);

  // Set employeeId when employee is selected
  useEffect(() => {
    if (selectedEmp && selectedEmp.employeeId) {
      setValue("employeeId", selectedEmp.employeeId);
    }
  }, [selectedEmp, setValue]);

  const newIssueDate = (issueDate) => {
    let date = moment(issueDate);

    if(date){
      let dateVal = moment(date).format("yyyy-MM-DD");
      // Expiration date is calculated by the backend based on plan settings
      setIssueDate(dateVal);
    }
  }

  const handleEmployeeSelect = (employee) => {
    setSelectedEmp(employee);
  }

  const addEmpOption = (data) => 
  {
    createEmpOption(data)
    .then(() => {
      history.push(`/options/plans/${planId}`);
    })
    .catch(() => {
      history.push(`/options/plans/${planId}`);
    });
  }

  return (
    <Container className="mt-4">
      <Row className="justify-content-center">
        <Col lg={8} xl={6}>
          {isDataLoading ? (
            <Card className="text-center py-5">
              <Card.Body>
                <Spinner animation="border" variant="primary" className="mb-3" />
                <div>Loading option group details...</div>
              </Card.Body>
            </Card>
          ) : (
            <Card className="shadow-sm border-0">
              <Card.Body className="p-4">
                <Form noValidate onSubmit={handleSubmit(addEmpOption)}>
                  {/* Employee Selection Section */}
                  <div className="mb-4">
                    <h5 className="text-primary mb-3">Employee Information</h5>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">Employee Name *</Form.Label>
                      <EmployeeSearchAdmin
                        onSelect={handleEmployeeSelect}
                        preselectedEmployee={selectedEmp}
                      />
                      <Form.Control 
                        type="hidden" 
                        isInvalid={!!errors.employeeId} 
                        {...register("employeeId")} 
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.employeeId?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </div>

                  {/* Option Details Section */}
                  <div className="mb-4">
                    <h5 className="text-primary mb-3">Option Details</h5>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-bold">Issue Date *</Form.Label>
                          <Form.Control 
                            defaultValue={issueDate} 
                            isInvalid={!!errors.issueDate} 
                            type="date" 
                            className="form-control-lg"
                            {...register("issueDate")} 
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.issueDate?.message}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-bold">Total Shares *</Form.Label>
                          <Form.Control 
                            defaultValue={group.defaultNumberOfShares} 
                            isInvalid={!!errors.totalShares} 
                            type="number" 
                            placeholder="Enter number of shares"
                            className="form-control-lg"
                            {...register("totalShares")} 
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.totalShares?.message}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={12}>
                        <div className="p-3 bg-light rounded border">
                          <small className="text-muted">
                            Stock price and strike price will be calculated automatically based on the issue date and plan settings.
                          </small>
                        </div>
                      </Col>
                    </Row>
                  </div>

                  {/* Additional Information Section */}
                  <div className="mb-4">
                    <h5 className="text-primary mb-3">Additional Information</h5>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">Notes (Optional)</Form.Label>
                      <Form.Control 
                        as="textarea"
                        rows={3}
                        isInvalid={!!errors.notes} 
                        placeholder="Add any additional notes or comments about this option grant..."
                        {...register("notes")} 
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.notes?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </div>

                  <Form.Group>
                    <Form.Control value={group.issueGroupId} type="hidden" {...register("issueGroupId")} />
                  </Form.Group>

                  {/* Action Buttons */}
                  <div className="d-flex justify-content-between pt-3 border-top">
                    <Button 
                      variant="outline-secondary" 
                      size="lg"
                      onClick={() => history.push(`/options/plans/${planId}`)}
                    >
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      variant="primary" 
                      size="lg"
                      className="px-4"
                    >
                      Create Option Agreement
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>
    </Container>
  );
}